import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import HeroSection from './components/HeroSection';
import StatsBar from './components/StatsBar';
import StudentCard from './components/StudentCard';
import ImprovementChart from './components/ImprovementChart';
import CtaBanner from './components/CtaBanner';
import { studentStories, subjectFilters } from './data/storiesData';
import SEOHead from '../../components/SEOHead';

export default function SuccessStoriesPage() {
  const [activeFilter, setActiveFilter] = useState('All');
  const navigate = useNavigate();
  const siteUrl = (import.meta.env.VITE_SITE_URL as string) || 'https://example.com';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Student Success Stories | Mathematics Transformation | Augustine Anyimadu-Ahenkae',
    description:
      'Real student success stories showing dramatic math score improvements. See how Augustine Anyimadu-Ahenkae\'s teaching method transformed students\' grades in Algebra, Geometry, SAT, and AP Calculus.',
    url: `${siteUrl}/success-stories`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Success Stories', item: `${siteUrl}/success-stories` },
      ],
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      ratingCount: '300',
      bestRating: '5',
      worstRating: '1',
    },
  };

  const filtered = activeFilter === 'All'
    ? studentStories
    : studentStories.filter(s => s.tags.some(t => t.toLowerCase().includes(activeFilter.toLowerCase())));

  return (
    <div className="min-h-screen bg-white font-sans">
      <SEOHead
        title="Student Success Stories | Math Score Improvements New York | Augustine Anyimadu-Ahenkae"
        description="Discover how students improved math scores by 20-40 points with Augustine Anyimadu-Ahenkae. Real success stories covering Algebra Regents, SAT math, AP Calculus, and K-12 math education in New York."
        canonical="/success-stories"
        keywords="math success stories, student improvement, Algebra Regents results, SAT math improvement, math tutoring results New York, student testimonials"
        jsonLd={jsonLd}
      />
      <Navbar />

      <HeroSection />
      <StatsBar />

      {/* Stories Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-extrabold text-gray-900 mb-3">Student Transformations</h2>
            <p className="text-gray-500 text-base max-w-xl mx-auto">
              Filter by subject to find stories most relevant to your student's needs.
            </p>
          </div>

          {/* Filter Tabs */}
          <div className="flex flex-wrap gap-2 justify-center mb-12">
            {subjectFilters.map((f) => (
              <button
                key={f}
                onClick={() => setActiveFilter(f)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeFilter === f
                    ? 'bg-emerald-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-emerald-50 hover:text-emerald-700'
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          {/* Cards */}
          {filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <i className="ri-search-line text-4xl mb-3 block"></i>
              <p className="text-base">No stories found for this filter.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((story) => (
                <StudentCard key={story.id} story={story} />
              ))}
            </div>
          )}
        </div>
      </section>

      <ImprovementChart />

      {/* Video / Quote Highlight */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900 mb-3">What Parents Are Saying</h2>
            <p className="text-gray-500 text-base">Trusted by families across New York and beyond.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                quote: "My son went from a 58 to a 91 on his Algebra Regents. I've tried other tutoring services — nothing compares to this program.",
                name: 'Sandra T.',
                role: 'Parent of Grade 9 student',
                img: 'https://public.readdy.ai/ai/img_res/16041a2fbc6fe7a6f0e3e2aa59295e31.jpg',
              },
              {
                quote: "The lessons are clear, structured, and actually fun. My daughter now asks to do her math homework. That alone is a miracle.",
                name: 'Maria M.',
                role: 'Parent of Grade 5 student',
                img: 'https://readdy.ai/api/search-image?query=professional%20woman%20parent%20smiling%20portrait%20clean%20white%20background%20warm%20natural%20light&width=80&height=80&seq=par2&orientation=squarish',
              },
              {
                quote: "Priya got into her first-choice engineering program. Math Mastery gave her the foundation she needed to succeed at the highest level.",
                name: 'Raj N.',
                role: 'Parent of Grade 12 student',
                img: 'https://readdy.ai/api/search-image?query=professional%20man%20parent%20smiling%20portrait%20clean%20white%20background%20warm%20natural%20light&width=80&height=80&seq=par3&orientation=squarish',
              },
            ].map((p, i) => (
              <div key={i} className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 border border-emerald-100 flex flex-col gap-4">
                <div className="flex gap-1">
                  {[1,2,3,4,5].map(s => (
                    <i key={s} className="ri-star-fill text-amber-400 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-gray-700 italic leading-relaxed flex-1">"{p.quote}"</p>
                <div className="flex items-center gap-3 pt-2 border-t border-emerald-100">
                  <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-emerald-200 shrink-0">
                    <img src={p.img} alt={p.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">{p.name}</p>
                    <p className="text-xs text-gray-500">{p.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust badges */}
      <section className="py-12 bg-gray-50 border-t border-gray-100">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: 'ri-shield-check-line', label: '30+ Years Teaching Experience', color: 'text-emerald-600' },
              { icon: 'ri-award-line', label: "NYC Chancellor's Honor Award", color: 'text-amber-500' },
              { icon: 'ri-book-open-line', label: 'K–12 Full Curriculum Coverage', color: 'text-teal-600' },
              { icon: 'ri-customer-service-2-line', label: 'Personal Tutoring Available', color: 'text-rose-500' },
            ].map((b, i) => (
              <div key={i} className="flex flex-col items-center text-center gap-2">
                <div className={`w-12 h-12 flex items-center justify-center rounded-full bg-white shadow-sm ${b.color}`}>
                  <i className={`${b.icon} text-xl`}></i>
                </div>
                <p className="text-xs font-semibold text-gray-600 leading-snug">{b.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner />

      {/* Back to home */}
      <div className="py-8 bg-white text-center">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-emerald-600 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          Back to Homepage
        </button>
      </div>

      <Footer />
    </div>
  );
}
